import { toast } from 'react-toastify';
import humanizeDuration from 'humanize-duration';
import { differenceInMilliseconds, addMinutes, format } from 'date-fns';
import CheckType from '../models/CheckType';
import {
	CheckTypeAvailability,
	NameTitles,
	Outcome,
	AuStateCode,
	SecondaryDocuments,
	Status,
	finishedStatuses,
	pendingStatuses,
	AdditionalNameTypes,
	candidateActionStatuses
} from './enums';
import {
	hasCustomerRole,
	getRefereeTokenAndWorkflowIdFromQuery,
	setRefereeTokenAndWorkflowIdInLocalStorage,
	getRefereeToken,
	getWorkflowId,
	getSetPasswordQueryStringsFromStorage,
	getSetPasswordQueryStringsFromQuery,
	setSetPasswordQueryStringsInLocalStorage,
	getInvitationQueryStringsFromQuery,
	setInvitationQueryStringsInLocalStorage,
	getInvitationQueryStringsFromStorage
} from './session';
import {
	hasValue,
	getLocaleDate,
	maxDate,
	saveBase64File,
	getIntFromQueryString,
	nestedArrayReducer,
	replaceElementInArray,
	isNullOrEmptyString
} from '../lib/utils';
import Blob from '../models/be/db/Blob';
import Document from '../models/be/db/Document';
import {
	confirmIdentityAction,
	verifyIdentityAction,
	cref,
	ukCountryCode,
	auCountryCode,
	dbs,
	icrm,
	icrd,
	anis,
	anie,
	centralAmericanCountries,
	southAmericanCountries,
	euCountries
} from './constants';
import Option from '../models/Option';
import ReferenceWorkflow from '../models/be/ReferenceWorkflow';
import Reference from '../models/be/Reference';
import { getFile, generateNewRefereeToken, setSectionComponentIdsToBeUsedAsReactKeys } from './api';
import { statuses } from '../lib/ApiBase';
import AdditionalName from '../models/be/AdditionalName';
import FormSpecification from '../models/be/db/FormSpecification';
import AddressHistory, { AddressHistoryUiModel } from '../models/be/AddressHistory';
import { isCustomWorkflow } from './workflows';
import PrimaryName from '../models/be/PrimaryName';

export const userCanCancelCheck = (status: Status) => {
	return !isFinishedWorkflowStatus(status) && hasCustomerRole();
};

export const getEnabledAddableCheckTypes = (checkTypes: CheckType[]) => {
	return getEnabledCheckTypes(checkTypes).filter((x) => x.packageOnly === false && x.isChild === false);
};

const getEnabledCheckTypes = (checkTypes: CheckType[]) => {
	// REF child workflow templates don't have PDS records, so serviceInclude will be null for them.
	return checkTypes?.filter(
		(x) => x.serviceIncluded === CheckTypeAvailability.EnabledForCustomer || x.serviceIncluded === null
	);
};

export const getEnabledPackageCheckType = (checkTypes: CheckType[]) => {
	return getEnabledCheckTypes(checkTypes)?.filter((x) => x.isChild === false);
};

// Gender value must match with BE Model "BirthDetails" RegularExpression string pattern.
export const getGenderList = (
	shouldShowTwoGenderOptions: boolean = null,
	nameTitle: string = null
): Option<string>[] => {
	if (shouldShowTwoGenderOptions) {
		if (nameTitle === NameTitles.Mr) return [{ text: 'Male', value: 'male' }];
		if (nameTitle === NameTitles.Mrs || nameTitle === NameTitles.Ms || nameTitle === NameTitles.Miss) {
			return [{ text: 'Female', value: 'female' }];
		}
		return [
			{ text: 'Male', value: 'male' },
			{ text: 'Female', value: 'female' }
		];
	}
	return [
		{ text: 'Male', value: 'M' },
		{ text: 'Female', value: 'F' },
		{ text: 'Indeterminate/Intersex/Unspecified', value: 'X' }
	];
};

export const referenceTypes: Option<string>[] = [
	{ text: 'Reference - Employment', value: 'EREF' },
	{ text: 'Reference - Education', value: 'EDREF' },
	{ text: 'Reference - Self Employed', value: 'SEREF' },
	{ text: 'Reference - Agency', value: 'AREF' },
	{ text: 'Reference - Personal', value: 'PREF' },
	{ text: 'Career Break', value: 'CREF' }
];

export const australianStates = [
	{ code: AuStateCode.Act, name: 'Australian Capital Territory' },
	{ code: AuStateCode.Nsw, name: 'New South Wales' },
	{ code: AuStateCode.Nt, name: 'Northern Territory' },
	{ code: AuStateCode.Qld, name: 'Queensland' },
	{ code: AuStateCode.Sa, name: 'South Australia' },
	{ code: AuStateCode.Tas, name: 'Tasmania' },
	{ code: AuStateCode.Vic, name: 'Victoria' },
	{ code: AuStateCode.Wa, name: 'Western Australia' }
];

const australianAgencies = ['Army', 'Commonwealth', 'Defence Force Academy', 'Navy', 'RAAF', 'Overseas', 'Other'];

export const getAustralianStateCodes = () => {
	return australianStates.map((x) => x.code);
};

export const getAustralianStateCodesAndAgency = () => {
	const stateCode = getAustralianStateCodes() as string[];
	return stateCode?.concat(australianAgencies);
};

export const getMedicareTypeList = (): Option<string>[] => [
	{ text: 'Yellow', value: 'Y' },
	{ text: 'Green', value: 'G' },
	{ text: 'Blue', value: 'B' }
];

export const getPassportTypeList = (): Option<string>[] => [
	{ text: 'Private Usage', value: 'Private' },
	{ text: 'Government Business', value: 'Government' },
	{ text: 'UN Refugee', value: 'UN Refugee' }
];

export const getGenderOption = (value: string, isDbsWorkflowOrCountryIsUk: boolean = null): Option<string> => {
	return getGenderList(isDbsWorkflowOrCountryIsUk)?.find((x) => x.value === value);
};

// Attribution: rjanjic
// https://stackoverflow.com/questions/150033/regular-expression-to-match-non-ascii-characters
const allLanguageRegexString =
	'\u0041-\u005A\u0061-\u007A\u00AA\u00B5\u00BA\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u0527\u0531-\u0556\u0559\u0561-\u0587\u05D0-\u05EA\u05F0-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u08A0\u08A2-\u08AC\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0977\u0979-\u097F\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C33\u0C35-\u0C39\u0C3D\u0C58\u0C59\u0C60\u0C61\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D60\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F4\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u1700-\u170C\u170E-\u1711\u1720-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1877\u1880-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191C\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19C1-\u19C7\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4B\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1CE9-\u1CEC\u1CEE-\u1CF1\u1CF5\u1CF6\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312D\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FCC\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA697\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA78E\uA790-\uA793\uA7A0-\uA7AA\uA7F8-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA80-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uABC0-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC';

export const getEnglishNamesRegexString = () => {
	// This regex has been duplicated in AcicChecks FnApp and should be kept the same.
	return "(^[a-zA-Z]+$)|(^[a-zA-Z][a-zA-Z '\\-]*[a-zA-Z]$)";
};

export const englishNamesRegexErrorMessage =
	'Please enter valid Name with alphabet characters, spaces, apostrophes and hyphens only.';

export const birthPlaceRegexString = `(^[a-zA-Z]+$)|(^[a-zA-Z][a-zA-Z '\\-]*[a-zA-Z]$)`;

export const birthPlaceRegexErrorMessage =
	'Please enter birth place with alphabet characters, spaces, apostrophes and hyphens only.';

export const dbsTextRegexString = `(^$|^[${allLanguageRegexString}0-9()-/'&]+$)|(^[${allLanguageRegexString}0-9()-/'&][${allLanguageRegexString} 0-9()-/'&]*[${allLanguageRegexString}0-9()-/'&]$)`;

export const dbsTextRegexErrorMessage =
	'Please enter valid value with alphabet characters, numbers, spaces, parentheses, slashes, apostrophes, ampersands and hyphens only.';

export const dbsDsPostcodeRegexString =
	'^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2}[A-Za-z]{0,2})|([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([AZa-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$';

export const dbsDsPostcodeRegexErrorMessage =
	'Please enter valid Postcode with alphabet characters, numbers and spaces only.';

export const nationalInsuranceNumberRegexString =
	'^(?!BG|GB|NK|KN|TN|NT|ZZ)[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z](?:\\s*\\d{2}){3}\\s*[A-D]$';

export const nationalInsuranceNumberRegexErrorMessage =
	'Please enter valid National Insurance Number with alphabet characters, numbers and spaces only.';

export const dsTextRegexString = `^([${allLanguageRegexString}0-9&',. -]*)$`;

export const dsTextRegexErrorMessage =
	'Please enter valid value with alphanumeric, ampersands, apostrophes, commas, full stops, hyphens and spaces only.';

// Copy to CreateCustomerCommand.cs / UpdateCustomerCommand.cs
export const customerNameRegexString = `^([${allLanguageRegexString}0-9&',./() -]*)$`;

export const customerNameRegexErrorMessage =
	'Please enter valid value with alphanumeric, ampersands, apostrophes, commas, forward slashes, full stops, hyphens, round brackets and spaces only.';

export const dsStreetNameAddressRegexString = `^([${allLanguageRegexString}0-9&'\\\\,/() \\[\\]-]*)$`;

export const dsStreetNameAddressRegexErrorMessage =
	"Value can only include letters, numbers or these special characters: &'/,/-()[]";

export const dsAddressRegexString = `^([${allLanguageRegexString}0-9&'\\\\,/.() \\[\\]-]*)$`;

export const dsAddressRegexErrorMessage =
	'Please enter valid value with alphanumeric, ampersand, apostrophes, back slash, commas, forward slash, full stops, hyphens, round brackets, spaces and square brackets only.';

export const alphanumericRegexString = '^([a-zA-Z0-9]*)$';

export const alphanumericRegexErrorMessage = 'Please enter valid value with alphanumeric only.';

// Copy to WorkflowTemplate.cs
export const alphanumericWithSpacesRegexString = '^[a-zA-Z0-9 ]*$';

export const alphanumericWithSpacesRegexErrorMessage =
	'Please enter a valid value with alphabet letters, numbers and spaces only.';

export const numericRegexString = '^([0-9]*)$';

export const numericRegexErrorMessage = `Please enter only the numbers with no '/' characters.`;

export const getFileBlobName = (blob: Blob) => {
	if (hasValue(blob)) {
		return blob?.originalFileName;
	}
	return null;
};

export const isUkCountryCode = (countryCode: string) => {
	return countryCode === ukCountryCode;
};

export const isAuCountryCode = (countryCode: string) => {
	return countryCode === auCountryCode;
};

export const isOutcomeClearType = (outcome: string) => {
	return (
		outcome === Outcome.ClearResult ||
		outcome === Outcome.ClearResultConfirmed ||
		outcome === Outcome.ResultMarkedAsClear
	);
};

export const isConfirmIdentityAction = (action: string) => {
	return action === confirmIdentityAction;
};

export const isVerifyIdentityAction = (action: string) => {
	return action === verifyIdentityAction;
};

export const isFinishedWorkflowStatus = (status: Status) => {
	const isFinished = finishedStatuses.includes(status);
	if (isFinished) return true;

	const isPending = pendingStatuses.includes(status);
	if (!isPending) throw new Error(`Unexpected status: ${status}`);
	return false;
};

export const isInProgressFromCandidatePerspective = (status: Status) => {
	return candidateActionStatuses.includes(status) && status !== Status.NotStarted;
};

export const isStripeCardDocumentType = (documentType: string) => {
	return documentType === SecondaryDocuments.BankCard || documentType === SecondaryDocuments.CreditCard;
};

export const isCompletedDocument = (document: Document) => {
	if (!hasValue(document?.documentType)) {
		return false;
	}
	if (isStripeCardDocumentType(document.documentType)) {
		return hasValue(document.stripeCardDetails);
	}
	return hasValue(document.blob);
};

export const getAuStateCode = (value: string) => {
	if (!hasValue(value)) return null;
	if (australianStates?.find((x) => x.code?.toLowerCase() === value?.toLowerCase())) return value.toUpperCase();
	const auState = australianStates?.find((x) => x.name?.toLowerCase() === value?.toLowerCase());
	if (hasValue(auState)) return auState.code;
	return null;
};

export const getAuStateName = (code: string) => {
	const auState = australianStates?.find((x) => x.code?.toLowerCase() === code?.toLowerCase());
	if (hasValue(auState)) return auState.name.toLocaleUpperCase();
	return null;
};

export const getAuStateCodeOrAgency = (value: string) => {
	if (!hasValue(value)) return null;
	const stateCode = getAuStateCode(value);
	if (hasValue(stateCode)) return stateCode;
	return australianAgencies?.find((x) => x?.toLowerCase() === value?.toLowerCase());
};

export const getAscendingDateRangeAddressHistories = (addressHistories: AddressHistory[]) => {
	if (addressHistories?.find((x) => x.fromDate === null)) throw new Error('Address History date should not be null.');

	const dateRangeArray = addressHistories.map((x) => {
		return {
			from: new Date(x.fromDate),
			to: new Date(x.toDate),
			...x
		};
	});

	if (dateRangeArray.length <= 1) return dateRangeArray;

	return dateRangeArray.sort((a, b) => a.from.getTime() - b.from.getTime());
};

export const getAscendingAddressHistories = (addressHistories: AddressHistory[]) => {
	const ascendingDateRangeArray = getAscendingDateRangeAddressHistories(addressHistories);
	return ascendingDateRangeArray.map((x: AddressHistoryUiModel) => ({
		buildingName: x.buildingName,
		country: x.country,
		fromDate: x.fromDate,
		locality: x.locality,
		postcode: x.postcode,
		state: x.state,
		streetName: x.streetName,
		streetNumber: x.streetNumber,
		suburb: x.suburb,
		toDate: x.toDate,
		unitNumber: x.unitNumber
	}));
};

export const getDateDifferences = (from: string | Date, to: string | Date) => {
	const fromDate = typeof from === 'string' ? new Date(from) : from;
	const toDate = typeof to === 'string' ? new Date(to) : to;
	const diffInMilliseconds = differenceInMilliseconds(
		addMinutes(fromDate, fromDate.getTimezoneOffset()),
		addMinutes(toDate, toDate.getTimezoneOffset())
	);
	return humanizeDuration(diffInMilliseconds, { units: ['y', 'mo', 'd'], maxDecimalPoints: 0 });
};

// Copied to the Workflow.cs. Please keep them the same.
export const hasGaps = (referenceWorkflow: ReferenceWorkflow, hasReferenceCompleted: boolean): boolean => {
	const [gapsExistBeforeReferences, gapExistsAfterLastReference] = getGaps(referenceWorkflow, hasReferenceCompleted);
	return gapsExistBeforeReferences.some((x) => x) || gapExistsAfterLastReference;
};

// Copied to the Workflow.cs. Please keep them the same.
// TODO refactor this function to reduce duplication. e.g. gapsDurationBeforeReferences and gapsExistBeforeReferences
export const getGaps = (
	referenceWorkflow: ReferenceWorkflow,
	hasReferenceCompleted: boolean
): [boolean[], boolean, string[], string] => {
	if (!referenceWorkflow.coverageYearsRequired || hasReferenceCompleted) {
		return [referenceWorkflow.references.map(() => false), false, referenceWorkflow.references.map(() => null), null];
	}
	const validReferences = getValidReferencesWithCareerBreak(
		referenceWorkflow.references,
		referenceWorkflow.reviewCandidateInput
	);
	const [coverageStartDate, coverageEndDate] = getCoverageStartDateAndEndDate(referenceWorkflow);
	if (validReferences.length === 0) {
		// If no reference, the minimum gap duration is one day, so minus one day to coverageStartDate.
		// e.g. from 01/01/2020 to 01/01/2020 is one day duration
		coverageStartDate.setDate(coverageStartDate.getDate() - 1);
		return [
			referenceWorkflow.references.map(() => false),
			true,
			referenceWorkflow.references.map(() => null),
			getDateDifferences(coverageStartDate, coverageEndDate)
		];
	}

	let allowedGapInMilliseconds = 0;
	if (referenceWorkflow.gapsAllowed) {
		allowedGapInMilliseconds = getAllowedGapInMilliseconds(
			referenceWorkflow.allowedGapLength,
			referenceWorkflow.allowedGapPeriod
		);
	}

	const sortedReferences = validReferences.sort(
		(a, b) =>
			getLocaleDate(a.capturedReferenceDetails.fromDate).getTime() -
			getLocaleDate(b.capturedReferenceDetails.fromDate).getTime()
	);

	const gapBetweenCoverageStartDateAndFirstFromDate =
		getLocaleDate(sortedReferences[0].capturedReferenceDetails.fromDate).getTime() - coverageStartDate.getTime();
	const gapExistsBeforeFirstReference = gapBetweenCoverageStartDateAndFirstFromDate > allowedGapInMilliseconds;
	const gapsExistBeforeReferences = [gapExistsBeforeFirstReference];

	let gapDurationBeforeFirstReference = null;
	if (gapExistsBeforeFirstReference) {
		const gapStartDate = getLocaleDate(sortedReferences[0].capturedReferenceDetails.fromDate);
		gapDurationBeforeFirstReference = getDateDifferences(gapStartDate, coverageStartDate);
	}

	const gapsDurationBeforeReferences = [gapDurationBeforeFirstReference];

	let previousEndDate = maxDate(getLocaleDate(sortedReferences[0].capturedReferenceDetails.toDate), coverageStartDate);
	const millisecondsInADay = 1000 * 60 * 60 * 24;
	// Start from index 1 as the gap before the first reference is set already in above gapExistsBeforeFirstReference.
	// eslint-disable-next-line no-plusplus
	for (let i = 1; i < sortedReferences.length; i++) {
		const reference = sortedReferences[i];
		const gapBetweenFromDateAndPreviousEndDate =
			getLocaleDate(reference.capturedReferenceDetails.fromDate).getTime() - previousEndDate.getTime();
		// The minimum gap of two consective days is 24 hours, so add millisecondsInADay to allowedGapInMilliseconds.
		const gapExists = gapBetweenFromDateAndPreviousEndDate > allowedGapInMilliseconds + millisecondsInADay;
		gapsExistBeforeReferences.push(gapExists);

		let gapsDurationBeforeReference = null;
		if (gapExists) {
			const gapStartDate = getLocaleDate(reference.capturedReferenceDetails.fromDate);
			// The minimum gap of two consective days is 24 hours, so add one day to gapStartDate.
			gapStartDate.setDate(gapStartDate.getDate() + 1);
			gapsDurationBeforeReference = getDateDifferences(gapStartDate, previousEndDate);
		}
		gapsDurationBeforeReferences.push(gapsDurationBeforeReference);

		const toDate = getLocaleDate(reference.capturedReferenceDetails.toDate);
		if (toDate > previousEndDate) {
			previousEndDate = toDate;
		}
	}

	const gapBetweenCoverageEndDateAndLastEndDate = coverageEndDate.getTime() - previousEndDate.getTime();
	const gapExistsAfterLastReference = gapBetweenCoverageEndDateAndLastEndDate > allowedGapInMilliseconds;
	const gapsDurationAfterLastReference = gapExistsAfterLastReference
		? getDateDifferences(previousEndDate, coverageEndDate)
		: null;
	gapsExistBeforeReferences.reverse(); // Reverse the order to match the order of the references.
	// Timeline page includes both valid and invalid references, but Gaps are calculated for valid references only,
	// set gap to false for invalid references, so that Timeline view page can display the Gaps in correct places.
	gapsDurationBeforeReferences.reverse();
	insertFalseForGapsExistBeforeInvalidReferences(
		gapsExistBeforeReferences,
		validReferences,
		referenceWorkflow.references,
		gapsDurationBeforeReferences
	);
	return [
		gapsExistBeforeReferences.reverse(),
		gapExistsAfterLastReference,
		gapsDurationBeforeReferences.reverse(),
		gapsDurationAfterLastReference
	];
};

const getCoverageStartDateAndEndDate = (referenceWorkflow: ReferenceWorkflow): [Date, Date] => {
	const millisecondsInADay = 1000 * 60 * 60 * 24;
	const coverageStartDate = new Date();
	coverageStartDate.setHours(0, 0, 0, 0);
	coverageStartDate.setUTCFullYear(coverageStartDate.getFullYear() - referenceWorkflow?.requiredCoverageYears);
	coverageStartDate.setTime(coverageStartDate.getTime() + millisecondsInADay);
	const coverageEndDate = new Date();
	coverageEndDate.setHours(0, 0, 0, 0);
	return [coverageStartDate, coverageEndDate];
};

// When validate numbers of valid references, should filter out Career Break.
export const getValidReferencesWithoutCareerBreak = (references: Reference[], reviewCandidateInput: boolean) => {
	const validReferencesWithCareerBreak = getValidReferencesWithCareerBreak(references, reviewCandidateInput);
	return validReferencesWithCareerBreak.filter((r) => r.workflowTypeCode !== cref);
};

// When validate references gaps, should include Career Break.
const getValidReferencesWithCareerBreak = (references: Reference[], reviewCandidateInput: boolean) => {
	return references.filter(
		(r) =>
			(r.refereeAccept !== false && r.status !== Status.Cancelled) ||
			(reviewCandidateInput && r.status !== Status.Cancelled && !r.refereeAccept)
	);
};

const insertFalseForGapsExistBeforeInvalidReferences = (
	gapsExistBeforeValidReferences: boolean[],
	validReferences: Reference[],
	allReferences: Reference[],
	gapsDurationBeforeReferences: string[]
) => {
	// eslint-disable-next-line no-plusplus
	for (let i = 0; i < allReferences.length; i++) {
		if (!validReferences.some((vr) => vr.id === allReferences[i].id)) {
			gapsExistBeforeValidReferences.splice(i, 0, false);
			gapsDurationBeforeReferences.splice(i, 0, null);
		}
	}
	return [gapsExistBeforeValidReferences, gapsDurationBeforeReferences];
};

// Copied to the ReferenceService.cs. Please keep them the same.
const getAllowedGapInMilliseconds = (gapLength: number, gapPeriod: string) => {
	const millisecondsInADay = 1000 * 60 * 60 * 24;
	const daysInAWeek = 7;
	switch (gapPeriod) {
		case 'days':
			return gapLength * millisecondsInADay;
		case 'weeks':
			return gapLength * millisecondsInADay * daysInAWeek;
		default:
			throw new Error(`Unknown gap period: ${gapPeriod}.`);
	}
};

// TODO: Replace code in DocumentReport with the generic function.
// TODO: this code is almost a duplicate of code in DocumentReport.
// This fn uses toasts.
export const downloadDocument = async (document: Document) => {
	let fileString;
	let documentName;
	if (hasValue(document)) {
		if (hasValue(document.blob)) {
			fileString = await getFile(document.blob.fileId);
			documentName = document.blob?.blobName;
		}
	}

	if (hasValue(fileString)) {
		saveBase64File(fileString, documentName);
	} else {
		// TODO: Since this is UI code, does this fn belong in domain.ts?
		toast('File is not found');
	}
};

export const getRefereeTokenFromQueryOrLocalStorage = async (location: any): Promise<[string, boolean]> => {
	// todo: fix this declaration.
	// eslint-disable-next-line prefer-const
	let [refereeToken, workflowId] = getRefereeTokenAndWorkflowIdFromQuery(location);
	if (hasValue(refereeToken)) {
		const clientCustomerId = getIntFromQueryString(location.search, 'clientCustomerId');

		try {
			refereeToken = await generateNewRefereeToken(refereeToken, clientCustomerId);
			setRefereeTokenAndWorkflowIdInLocalStorage(refereeToken, workflowId);
		} catch (e) {
			if (e.status === statuses.unauthorized) {
				const storedToken = getRefereeToken();
				const storedWorkflowId = getWorkflowId();
				// Only return the storedToken if the stored workflowId is the same as the workflow id in the URL.
				// This is only to account for a referee who has multiple references for multiple candidates at the same time.
				return hasValue(storedToken) && storedWorkflowId === workflowId ? [storedToken, true] : [refereeToken, false];
			}
			throw e;
		}
	}

	if (!hasValue(refereeToken)) {
		refereeToken = getRefereeToken();
	}
	return [refereeToken, hasValue(refereeToken)];
};

export const getSetPasswordQueryStringsFromQueryOrLocalStorage = (location: any): [string, string, string] => {
	let [secret, email, companyName] = getSetPasswordQueryStringsFromQuery(location);
	if (hasValue(secret)) {
		setSetPasswordQueryStringsInLocalStorage(secret, email, companyName);
	} else {
		[secret, email, companyName] = getSetPasswordQueryStringsFromStorage();
	}
	return [secret, email, companyName];
};

export const getInvitationQueryStringsFromQueryOrLocalStorage = (location: any): [string, string, string] => {
	let [invitationToken, workflowGroupId, companyName] = getInvitationQueryStringsFromQuery(location);
	if (hasValue(workflowGroupId)) {
		setInvitationQueryStringsInLocalStorage(invitationToken, workflowGroupId, companyName);
	} else {
		[invitationToken, workflowGroupId, companyName] = getInvitationQueryStringsFromStorage();
	}
	return [invitationToken, workflowGroupId, companyName];
};

export const screenerHasNotDecidedAVerdict = (outcome: string) => {
	return outcome === Outcome.ClearResult || outcome === Outcome.ReviewResult;
};

const formFullName = (name: { firstName: string; middleNames?: string; lastName: string }): string =>
	`${name.firstName}${name.middleNames || ''}${name.lastName}`.trim();

export const validateAdditionalNames =
	(
		primaryName: PrimaryName,
		additionalNames: AdditionalName[],
		selectedIndex: number | null,
		workflowTypeCode: string | null
	) =>
	(values: { additionalName: AdditionalName }) => {
		const { additionalName } = values;
		const primaryFullName = formFullName(primaryName);
		const additionalFullName = formFullName(additionalName);

		if (primaryFullName?.toLowerCase() === additionalFullName?.toLowerCase()) {
			return 'Please enter a different additional name from the primary name';
		}

		if (!hasValue(additionalName.lastName) && additionalName.type === 'Previous' && workflowTypeCode === dbs) {
			return 'Please enter your last name';
		}

		if (selectedIndex === null) {
			if (additionalNames.some((name) => isFullNameEqual(name, additionalFullName))) {
				return 'Additional name already exists';
			}
		} else if (
			additionalNames
				.filter((_, index) => index !== selectedIndex)
				.some((name) => isFullNameEqual(name, additionalFullName))
		) {
			return 'Additional name already exists';
		}

		if (hasValidDateRange(additionalName)) {
			return 'To date must not be before From date';
		}

		if (!validateMaidenNameUniqueness(replaceElementInArray(additionalNames, values.additionalName, selectedIndex))) {
			return 'Only one maiden name is allowed';
		}

		return null;
	};

function isFullNameEqual(name: AdditionalName, fullName: string) {
	return formFullName(name)?.toLowerCase() === fullName?.toLowerCase();
}

function hasValidDateRange(additionalName: AdditionalName) {
	return (
		hasValue(additionalName.fromDate) &&
		hasValue(additionalName.toDate) &&
		additionalName.fromDate > additionalName.toDate
	);
}

const validateMaidenNameUniqueness = (additionalNames: AdditionalName[]) => {
	return additionalNames.filter((x) => x.type === AdditionalNameTypes.Maiden).length <= 1;
};

export const validateAddressHistory = (addressHistories: AddressHistory[], selectedIndex: number) => (values: any) => {
	const { fromDate, toDate } = values.addressHistory;

	if (hasValue(fromDate) && hasValue(toDate) && fromDate > toDate) {
		return 'To date must not be before From date.';
	}
	if (
		!validateDateRangeOverlapping(
			generateUpdatedAscendingAddressHistories(addressHistories, values.addressHistory, selectedIndex)
		)
	) {
		return 'The date range overlaps other addresses.';
	}
	return null;
};

export const generateUpdatedAscendingAddressHistories = (
	addressHistories: AddressHistory[],
	newAddressHistory: AddressHistory,
	index: number
) => {
	const updatedAddressHistories = replaceElementInArray(addressHistories, newAddressHistory, index);
	return getAscendingAddressHistories(updatedAddressHistories);
};

const validateDateRangeOverlapping = (addressHistories: AddressHistory[]) => {
	const ascendingAddressHistories = getAscendingDateRangeAddressHistories(addressHistories);

	if (ascendingAddressHistories.length === 1) return true;

	const overlappedDateRange = ascendingAddressHistories?.find((x, i, array) => {
		if (i === 0) {
			return false;
		}
		const previous = array[i - 1];
		const current = array[i];
		return previous.to >= current.from;
	});
	return overlappedDateRange === undefined;
};

export const setFormSpecComponentIdsToBeUsedAsReactKeys = (updatedFormSpecification: FormSpecification) => {
	updatedFormSpecification.pages
		.map((p) => p.sections)
		.reduce(nestedArrayReducer, [])
		.forEach((s) => setSectionComponentIdsToBeUsedAsReactKeys(s));
};

export const formatDate = (date: string, formatString: string) => {
	if (isNullOrEmptyString(date)) {
		return null;
	}
	return format(new Date(date), formatString);
};

export const excludedWorkflowTypesForConsentDocumentDownload = [anis, anie];

export const canDownloadConsentDocument = (workflowTypeCode: string, signature: string) => {
	return (
		signature &&
		!isCustomWorkflow(workflowTypeCode) &&
		!excludedWorkflowTypesForConsentDocumentDownload.includes(workflowTypeCode)
	);
};

export interface ConsentDocumentConfig {
	key: string;
	label: string;
	props: {
		workflowId: string;
		exportAsPdf: boolean;
		downloadAISConsent?: boolean;
		downloadAISCountryConsent?: boolean;
		onSave?: () => void;
	};
	requiresRole?: boolean;
}

// Helper function to check if a country needs a separate AIS Country consent document
const needsSeparateAISCountryConsent = (country: string): boolean => {
	if (!country) return false;

	// Countries with specific consent documents
	const countriesWithSpecificConsents = ['BRA', 'CAN', 'CHN', 'FRA', 'DEU', 'SAU', 'ZAF', 'USA'];

	// Check if it's a country with specific consent
	if (countriesWithSpecificConsents.includes(country)) return true;

	// Check if it's in Central America
	if (centralAmericanCountries.includes(country)) return true;

	// Check if it's in South America (excluding Brazil which has its own)
	if (southAmericanCountries.includes(country)) return true;

	// Check if it's in EU
	if (euCountries.includes(country)) return true;

	return false;
};

export const getConsentDocumentConfigs = (
	workflowId: string,
	workflowTypeCode: string,
	signature: string,
	onSave?: () => void,
	checkCountry?: string
): ConsentDocumentConfig[] => {
	if (!canDownloadConsentDocument(workflowTypeCode, signature)) {
		return [];
	}

	const configs: ConsentDocumentConfig[] = [];

	// Standard consent document
	if (workflowTypeCode === icrm) {
		configs.push({
			key: 'downloadConsentDoc',
			label: 'Consent doc (Owens)',
			props: {
				workflowId,
				exportAsPdf: true,
				onSave
			},
			requiresRole: true // Requires CustomerSuccess role or higher
		});
	} else {
		configs.push({
			key: 'downloadConsentDoc',
			label: 'Download consent doc',
			props: {
				workflowId,
				exportAsPdf: true,
				onSave
			}
		});
	}

	// AIS consent document - only for ICRM/ICRD workflow types
	if (workflowTypeCode === icrm || workflowTypeCode === icrd) {
		configs.push({
			key: 'downloadAISConsentDoc',
			label: 'Consent doc (AIS)',
			props: {
				workflowId,
				exportAsPdf: true,
				downloadAISConsent: true,
				onSave
			}
		});

		// Country-specific AIS consent document - only for ICRM/ICRD with specific countries
		if (checkCountry && needsSeparateAISCountryConsent(checkCountry)) {
			configs.push({
				key: 'downloadAISCountryConsentDoc',
				label: 'Consent doc (AIS Country)',
				props: {
					workflowId,
					exportAsPdf: true,
					downloadAISConsent: true,
					downloadAISCountryConsent: true,
					onSave
				}
			});
		}
	}

	return configs;
};
